import React, { useMemo, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { usePerfil } from '../../context/PerfilContext';
import { usePlus } from '../../context/PlusContext';
import { formatoEuros, formatoPorcentaje } from '../../utils/numeros';
import {
  Balance, balance, mayoresSubidas, mediaMesesAnteriores, mesesAtras, periodoAnio, periodoMes, serieMensual, totalMes,
} from '../../utils/informes';
import {
  BloquePlus, EtiquetaPlus, Leyenda, mesCorto, TablaDatos, Tarjeta, TooltipEuros, VistaInforme, ejeEuros, useColoresGrafico,
} from './comun';

const nombreMes = (anio: number, mes: number) =>
  new Date(anio, mes - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

/** Un número del balance con su cambio frente al periodo anterior. */
const Cifra: React.FC<{
  titulo: string; valor: string; antes?: number; ahora: number; mejorSiSube: boolean; color: string; pie?: string;
}> = ({ titulo, valor, antes, ahora, mejorSiSube, color, pie }) => {
  const diff = antes === undefined ? 0 : ahora - antes;
  const igual = Math.abs(diff) < 0.005;
  const bueno = mejorSiSube ? diff > 0 : diff < 0;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-3 md:p-4">
      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{titulo}</p>
      <p className={`text-lg md:text-xl font-bold mt-1 tabular-nums ${color}`}>{valor}</p>
      {antes !== undefined && (
        <p className={`flex items-center gap-1 text-xs mt-0.5 ${igual ? 'text-gray-400' : bueno ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {igual ? <Minus className="w-3 h-3" /> : diff > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span className="tabular-nums">{pie ?? formatoEuros(diff, true)}</span>
        </p>
      )}
    </div>
  );
};

/** I1: ingresos, gastos, ahorro y tasa de ahorro del periodo, frente al anterior. */
const BalancePeriodo: React.FC<{ actual: Balance; anterior: Balance; etiquetaAnterior: string }> = ({ actual, anterior, etiquetaAnterior }) => {
  const difTasa = actual.tasa !== null && anterior.tasa !== null ? actual.tasa - anterior.tasa : null;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Cifra titulo="Ingresos" valor={formatoEuros(actual.ingresos)} ahora={actual.ingresos} antes={anterior.ingresos} mejorSiSube color="text-green-600 dark:text-green-400" />
        <Cifra titulo="Gastos" valor={formatoEuros(actual.gastos)} ahora={actual.gastos} antes={anterior.gastos} mejorSiSube={false} color="text-red-600 dark:text-red-400" />
        <Cifra titulo="Ahorro" valor={formatoEuros(actual.ahorro, true)} ahora={actual.ahorro} antes={anterior.ahorro} mejorSiSube
          color={actual.ahorro >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-400'} />
        <Cifra titulo="Tasa de ahorro" valor={actual.tasa === null ? '—' : formatoPorcentaje(actual.tasa)}
          ahora={actual.tasa ?? 0} antes={difTasa === null ? undefined : anterior.tasa ?? 0} mejorSiSube
          pie={difTasa === null ? undefined : `${difTasa > 0 ? '+' : ''}${Math.round(difTasa)} puntos`}
          color="text-gray-900 dark:text-white" />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Comparado con {etiquetaAnterior}. La tasa de ahorro es lo que te queda de cada euro que entra.
      </p>
    </div>
  );
};

/** I2 + C4: barras de ingresos y gastos de cada mes, con la línea del ahorro. */
const Evolucion: React.FC<{ anioFin: number; mesFin: number }> = ({ anioFin, mesFin }) => {
  const { transacciones } = useData();
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const c = useColoresGrafico();
  const [meses, setMeses] = useState(12);
  const serie = useMemo(() => serieMensual(transacciones, anioFin, mesFin, meses), [transacciones, anioFin, mesFin, meses]);
  const conDatos = serie.filter(p => p.ingresos > 0 || p.gastos > 0);
  const media = conDatos.length ? conDatos.reduce((s, p) => s + p.ahorro, 0) / conDatos.length : 0;

  const elegir = (n: number) => {
    if (n > 12 && !esPlus) { ofrecerPlus('informes'); return; }
    setMeses(n);
  };

  return (
    <Tarjeta
      titulo="Evolución"
      subtitulo={`Últimos ${meses} meses hasta ${nombreMes(anioFin, mesFin)}`}
      accion={
        <div className="flex gap-1 shrink-0">
          {[12, 24].map(n => (
            <button key={n} type="button" onClick={() => elegir(n)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition ${meses === n ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
              {n} meses
            </button>
          ))}
        </div>
      }
    >
      <Leyenda series={[
        { nombre: 'Ingresos', color: c.ingresos },
        { nombre: 'Gastos', color: c.gastos },
        { nombre: 'Ahorro', color: c.tinta, linea: true },
      ]} />
      <div className="h-56 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={serie} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke={c.rejilla} />
            <XAxis dataKey="etiqueta" tick={{ fontSize: 11, fill: c.suave }} tickLine={false} axisLine={{ stroke: c.rejilla }}
              interval={meses > 12 ? 2 : 0} />
            <YAxis tick={{ fontSize: 11, fill: c.suave }} tickLine={false} axisLine={false} tickFormatter={ejeEuros} width={56} />
            <ReferenceLine y={0} stroke={c.suave} />
            <Tooltip cursor={{ fill: c.rejilla, opacity: 0.5 }}
              content={<TooltipEuros titulo={l => {
                const p = serie.find(x => x.etiqueta === l);
                return p ? nombreMes(p.anio, p.mes) : l;
              }} />} />
            <Bar dataKey="ingresos" name="Ingresos" fill={c.ingresos} radius={[4, 4, 0, 0]} maxBarSize={18} />
            <Bar dataKey="gastos" name="Gastos" fill={c.gastos} radius={[4, 4, 0, 0]} maxBarSize={18} />
            <Line dataKey="ahorro" name="Ahorro" stroke={c.tinta} strokeWidth={2} dot={{ r: 3, fill: c.tinta, stroke: c.fondo, strokeWidth: 2 }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {conDatos.length > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
          De media ahorras <span className="font-semibold tabular-nums">{formatoEuros(media)}</span> al mes
          {' '}en los meses con movimientos.
        </p>
      )}
      <TablaDatos
        cabecera={['Mes', 'Ingresos', 'Gastos', 'Ahorro']}
        filas={serie.map(p => [mesCorto(p.anio, p.mes), formatoEuros(p.ingresos), formatoEuros(p.gastos), formatoEuros(p.ahorro, true)])}
      />
    </Tarjeta>
  );
};

/** I8: el mes frente al mismo mes del año pasado y la media de los 6 anteriores. */
const Comparativa: React.FC<{ anio: number; mes: number }> = ({ anio, mes }) => {
  const { transacciones } = useData();
  const filas = useMemo(() => {
    const fila = (tipo: 'gasto' | 'ingreso') => ({
      actual: totalMes(transacciones, tipo, anio, mes),
      anioPasado: totalMes(transacciones, tipo, anio - 1, mes),
      media: mediaMesesAnteriores(transacciones, tipo, anio, mes, 6),
    });
    const g = fila('gasto');
    const i = fila('ingreso');
    return [
      { nombre: 'Gastos', ...g, mejorSiSube: false },
      { nombre: 'Ingresos', ...i, mejorSiSube: true },
      { nombre: 'Ahorro', actual: i.actual - g.actual, anioPasado: i.anioPasado - g.anioPasado, media: i.media - g.media, mejorSiSube: true },
    ];
  }, [transacciones, anio, mes]);

  const color = (d: number, mejorSiSube: boolean) =>
    Math.abs(d) < 0.005 ? 'text-gray-400' : (mejorSiSube ? d > 0 : d < 0) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';

  return (
    <Tarjeta titulo="Comparado con lo habitual" subtitulo="Más estable que mirar solo el mes anterior: vacaciones, Navidad o seguros anuales se notan menos." accion={<EtiquetaPlus />}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-xs text-gray-500 dark:text-gray-400">
              <th className="text-left font-semibold py-1" />
              <th className="text-right font-semibold py-1 pl-3 capitalize">{new Date(anio, mes - 1, 1).toLocaleDateString('es-ES', { month: 'short' })} {anio}</th>
              <th className="text-right font-semibold py-1 pl-3 capitalize">{new Date(anio, mes - 1, 1).toLocaleDateString('es-ES', { month: 'short' })} {anio - 1}</th>
              <th className="text-right font-semibold py-1 pl-3">Media 6 meses</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filas.map(f => (
              <tr key={f.nombre}>
                <td className="py-2 font-medium text-gray-900 dark:text-white">{f.nombre}</td>
                <td className="py-2 pl-3 text-right font-semibold tabular-nums text-gray-900 dark:text-white">{formatoEuros(f.actual)}</td>
                <td className="py-2 pl-3 text-right tabular-nums">
                  <span className="text-gray-500 dark:text-gray-400">{formatoEuros(f.anioPasado)}</span>
                  <span className={`block text-xs ${color(f.actual - f.anioPasado, f.mejorSiSube)}`}>{formatoEuros(f.actual - f.anioPasado, true)}</span>
                </td>
                <td className="py-2 pl-3 text-right tabular-nums">
                  <span className="text-gray-500 dark:text-gray-400">{formatoEuros(f.media)}</span>
                  <span className={`block text-xs ${color(f.actual - f.media, f.mejorSiSube)}`}>{formatoEuros(f.actual - f.media, true)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Tarjeta>
  );
};

/**
 * I12: resumen de un mes cerrado: balance, ahorro y las tres categorías que
 * más han subido frente a su media. Es lo que abre la notificación del día 1.
 */
export const ResumenMes: React.FC<{ anio: number; mes: number }> = ({ anio, mes }) => {
  const { transacciones, categorias } = useData();
  const b = useMemo(() => balance(transacciones, periodoMes(anio, mes)), [transacciones, anio, mes]);
  const subidas = useMemo(() => mayoresSubidas(transacciones, categorias, anio, mes), [transacciones, categorias, anio, mes]);
  const [aA, mA] = mesesAtras(anio, mes);
  const antes = useMemo(() => balance(transacciones, periodoMes(aA, mA)), [transacciones, aA, mA]);

  return (
    <Tarjeta titulo={`Resumen de ${nombreMes(anio, mes)}`} accion={<EtiquetaPlus />}
      className="border-l-4 border-blue-500">
      {b.ingresos === 0 && b.gastos === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No apuntaste movimientos ese mes.</p>
      ) : (
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
          <p>
            Entraron <span className="font-semibold tabular-nums">{formatoEuros(b.ingresos)}</span> y
            {' '}salieron <span className="font-semibold tabular-nums">{formatoEuros(b.gastos)}</span>.
            {' '}{b.ahorro >= 0 ? 'Ahorraste' : 'Gastaste de más'}
            {' '}<span className={`font-semibold tabular-nums ${b.ahorro >= 0 ? '' : 'text-red-600 dark:text-red-400'}`}>{formatoEuros(Math.abs(b.ahorro))}</span>
            {b.tasa !== null && b.ahorro >= 0 && <> ({formatoPorcentaje(b.tasa)} de lo que entró)</>}.
          </p>
          <p className="text-gray-600 dark:text-gray-300">
            {b.gastos > antes.gastos + 0.005
              ? <>Gastaste {formatoEuros(b.gastos - antes.gastos)} más que el mes anterior.</>
              : b.gastos < antes.gastos - 0.005
                ? <>Gastaste {formatoEuros(antes.gastos - b.gastos)} menos que el mes anterior.</>
                : <>Gastaste lo mismo que el mes anterior.</>}
          </p>
          {subidas.length > 0 && (
            <div>
              <p className="text-gray-600 dark:text-gray-300">Lo que más subió frente a tu media de 6 meses:</p>
              <ul className="mt-1 space-y-1">
                {subidas.map(s => (
                  <li key={s.categoria.id} className="flex items-center gap-2">
                    <span>{s.categoria.icono}</span>
                    <span className="flex-1 truncate">{s.categoria.nombre}</span>
                    <span className="tabular-nums text-red-600 dark:text-red-400 font-semibold">{formatoEuros(s.diferencia, true)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Tarjeta>
  );
};

/** El último mes cerrado hasta el que se está viendo: si es el actual, el anterior. */
export function mesCerrado(anio: number, mes: number, hoy: Date = new Date()): [number, number] {
  const esActualOFuturo = anio > hoy.getFullYear() || (anio === hoy.getFullYear() && mes >= hoy.getMonth() + 1);
  return esActualOFuturo ? mesesAtras(hoy.getFullYear(), hoy.getMonth() + 1) : [anio, mes];
}

export const SeccionResumen: React.FC<{ vista: VistaInforme }> = ({ vista }) => {
  const { transacciones } = useData();
  const { esPlus } = usePerfil();
  const { anio, mes } = vista;

  const [actual, anterior, etiquetaAnterior] = useMemo((): [Balance, Balance, string] => {
    if (mes === null) {
      return [balance(transacciones, periodoAnio(anio)), balance(transacciones, periodoAnio(anio - 1)), String(anio - 1)];
    }
    const [aA, mA] = mesesAtras(anio, mes);
    return [balance(transacciones, periodoMes(anio, mes)), balance(transacciones, periodoMes(aA, mA)), nombreMes(aA, mA)];
  }, [transacciones, anio, mes]);

  const [anioFin, mesFin] = mes === null ? [anio, 12] : [anio, mes];
  const [anioCerrado, mesCerradoN] = mesCerrado(anioFin, mesFin);

  return (
    <div className="space-y-4">
      <BalancePeriodo actual={actual} anterior={anterior} etiquetaAnterior={etiquetaAnterior} />
      {esPlus
        ? <ResumenMes anio={anioCerrado} mes={mesCerradoN} />
        : <BloquePlus titulo="Resumen de cada mes" texto="Cada día 1, un resumen del mes que acaba: cuánto ahorraste y qué categorías se dispararon frente a lo habitual." />}
      <Evolucion anioFin={anioFin} mesFin={mesFin} />
      {mes !== null && (esPlus
        ? <Comparativa anio={anio} mes={mes} />
        : <BloquePlus titulo="Comparado con lo habitual" texto="Compara el mes con el mismo mes del año pasado y con tu media de los últimos 6 meses." />)}
    </div>
  );
};
